import PageLayout from "components/LayoutContainers/PageLayout";
import { useParams } from "react-router-dom";
import { act, useContext, useEffect, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { Card, Grid, InputLabel } from "@mui/material";
import SoftTypography from "components/SoftTypography";
import Equipamiento from "./Equipamiento";
import SoftButton from "components/SoftButton";
import { getColor } from "utils/colors";
import { MessageManager } from "context";
import MateriaPrima from "./MateriaPrima";
import Efluentes from "./Efluentes";
import Infraestructura from "./Infraestructura";
import Vehiculos from "./Vehiculos";
import MedidasDeSeguridad from "./MedidasDeSeguridad";
import ResiduosGenerados from "./ResiduosGenerados";
import ResiduosReciclados from "./ResiduosReciclados";
import ResiduosTransportados from "./ResiduosTransportados";
import ModalDomicilio from "./ModalDomicilio";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftBox from "components/SoftBox";
import { UserContext } from "context/user";
import ModalConfirm from "components/Modals/ConfirmMsg";

const GET_EMPRESA = gql`
  query getEmpresa($id: ID!) {
    empresa(id: $id) {
      __typename @skip(if: true)
      id
      razonSocial
      cuit
      email
      gerente
      codigoAfip
      registroCatastral
      habilitacionMunicipalNumero
      habilitacionMunicipalLugar
      calleLegal
      cpLegal
      provinciaLegal
      departamentoLegal
      localidadLegal
      telefonoLegal
      faxLegal
      calleReal
      cpReal
      provinciaReal
      departamentoReal
      localidadReal
      telefonoReal
      faxReal
    }
  }
`;
const GET_DECLARACION = gql`
  query getDeclaracion($id: ID!) {
    declaracionJurada(id: $id) {
      __typename @skip(if: true)
      id
      actividad
      estado
      calleLegal
      cpLegal
      provinciaLegal
      departamentoLegal
      localidadLegal
      telefonoLegal
      faxLegal
      calleReal
      cpReal
      provinciaReal
      departamentoReal
      localidadReal
      telefonoReal
      faxReal
      inicio
      fin
      anio
      cantidadManifiestos
      equipamientos {
        __typename @skip(if: true)
        id
        tipo
        potencia
        descripcion
        cantidad
      }
      materiasPrimas {
        __typename @skip(if: true)
        id
        compuestoQuimico
        unidad
        estado
        cantidadAnual
      }
      efluentesGaseosos {
        __typename @skip(if: true)
        id
        tipoDeResiduo
        descripcion
        dispositivoDeMedicion
        dispositivoDeDepuracion
        frecuenciaDeEmisiones
        tipoDeDispositivoDepuracion
        tipoDeDispositivoMedicion
        observaciones
        fileMedicion {
          id
          tipo
          file
        }
        fileDepuracion {
          id
          tipo
          file
        }
      }
      infraestructura {
        __typename @skip(if: true)
        id
        diasLaborales
        cantidadDeEmpleados
        aguaPotable
        aguaProcesoIndustrial
        potable
        especificacionPotable
        cruda
        especificacionCruda
        conexionCloacal
        cantidadConexionCloacal
        pozoAbsorvente
        cantidadPozoAbsorvente
        piletasLagunasDeTratamiento
        cantidadPiletasLagunasDeTratamiento
        plantaTratamiento
        cantidadPlantaTratamiento
        aguaNoIndustrial
        observacionAguaNoIndustrial
        aguaIndustrial
        observacionAguaIndustrial
        descripcionProcesoIndustrial
      }
      vehiculos {
        __typename @skip(if: true)
        id
        tipo
        marcaModelo
        dominio
        tallerPropio
      }
      medidasDeSeguridad {
        __typename @skip(if: true)
        id
        tipo
        descripcion
        observacion
      }
      residuosGenerados {
        __typename @skip(if: true)
        id
        idResiduo
        tipoResiduo
        descripcionResiduo
        cantidadResiduo
        unidadResiduo
        observacion
        idTratamiento
        tipoTratamiento
        descripcionTratamiento
        cantidadTratamiento
        unidadTratamiento
        idVehiculo
        idEmpresaVehiculo
        vehiculo
      }
      residuosReciclados {
        __typename @skip(if: true)
        id
        idResiduo
        tipoResiduo
        descripcionResiduo
        cantidadResiduo
        unidadResiduo
        idTratamiento
        tipoTratamiento
        descripcionTratamiento
        cantidadTratamiento
        unidadTratamiento
      }
      residuosTransportados {
        __typename @skip(if: true)
        id
        idResiduo
        tipoResiduo
        descripcionResiduo
        cantidadResiduo
        estadoFisico
        descripcionTransporteAuditoria
      }
    }
  }
`;

const SAVE_DECLARACION_JURADA = gql`
  mutation saveDeclaracionJurada($input: DeclaracionJuradaInput!) {
    saveDeclaracionJurada(input: $input) {
      id
    }
  }
`;
const ESTADO_DECLARACION_JURADA = gql`
  mutation actualizarEstadoDeclaracionJurada($input: DeclaracionJuradaInput!) {
    actualizarEstadoDeclaracionJurada(input: $input) {
      id
    }
  }
`;

const GET_TRANSPORTADOS = gql`
  query getResiduosTransportados($idEmpresa: ID!) {
    residuosTransportados(idEmpresa: $idEmpresa) {
      id
      tipoResiduo
      descripcionResiduo
      cantidadResiduo
      unidadResiduo
      observacion
      tipoTratamiento
      descripcionTratamiento
      cantidadTratamiento
      unidadTratamiento
    }
  }
`;

function DeclaracionJurada() {
  const { idEmpresa, idResponsable, actividad, id: idDeclaracion } = useParams();
  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);

  const [empresa, setEmpresa] = useState(null);
  const [periodos, setPeriodos] = useState(null);
  const [modalDomicilio, setModalDomicilio] = useState(false);
  const [modalExito, setModalExito] = useState(false);
  const [estadoGuardado, setEstadoGuardado] = useState(null);

  const [listadoEquipamiento, setListadoEquipamiento] = useState([]);
  const [listadoMateriaPrima, setListadoMateriaPrima] = useState([]);
  const [listadoEfluentes, setListadoEfluentes] = useState([]);
  const [listadoVehiculos, setListadoVehiculos] = useState([]);
  const [listadoMedidas, setListadoMedidas] = useState([]);
  const [listadoResiduosGenerados, setListadoResiduosGenerados] = useState([]);
  const [listadoResiduosReciclados, setListadoResiduosReciclados] = useState([]);
  const [listadoResiduosTransportados, setListadoResiduosTransportados] = useState([]);
  const [infraestructura, setInfraestructura] = useState({});
  const [isDeclaracionReady, setIsDeclaracionReady] = useState(idDeclaracion === "nuevo");
  const asNumberOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const [getEmpresa, { loading, data, error }] = useLazyQuery(GET_EMPRESA, {
    fetchPolicy: "no-cache",
  });
  const [
    getResiduosTransportados,
    { loading: loadingTransportados, data: dataTransportados, error: errorTransportados },
  ] = useLazyQuery(GET_TRANSPORTADOS, {
    fetchPolicy: "no-cache",
  });
  const [
    getDeclaracion,
    { loading: loadingDeclaracion, data: dataDeclaracion, error: errorDeclaracion },
  ] = useLazyQuery(GET_DECLARACION, {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });
  const [saveDeclaracionJurada, { loading: saving }] = useMutation(SAVE_DECLARACION_JURADA, {
    onCompleted: (data) => {
      handleSnackbar("Declaración jurada guardada correctamente", "success");
      if (estadoGuardado === "presentada") {
        setModalExito(true);
      } else {
        window.location.href = `/declaraciones-juradas`;
      }
    },
    onError: (error) => {
      console.error("Error al guardar la declaración jurada:", error);
      handleSnackbar(error.message || "No se pudo guardar la declaración jurada", "error");
    },
  });
  const [actualizarEstadoDeclaracionJurada, { loading: savingUpdate }] = useMutation(
    ESTADO_DECLARACION_JURADA,
    {
      onCompleted: (data) => {
        handleSnackbar("Declaración jurada guardada correctamente", "success");
        if (estadoGuardado === "presentada") {
          setModalExito(true);
        } else {
          window.location.href = `/declaraciones-juradas`;
        }
      },
      onError: (error) => {
        console.error("Error al guardar la declaración jurada:", error);
        handleSnackbar(error.message || "No se pudo actualizar la declaración jurada", "error");
      },
    }
  );

  const handleSave = (estado) => {
    setEstadoGuardado(estado);
    const input = {
      id: idDeclaracion !== "nuevo" ? idDeclaracion : undefined,
      idEmpresa,
      estado,
      idResponsableTecnico: idResponsable,
      calleLegal: empresa.calleLegal || "",
      cpLegal: empresa.cpLegal || "",
      provinciaLegal: empresa.provinciaLegal || "",
      departamentoLegal: empresa.departamentoLegal || "",
      localidadLegal: empresa.localidadLegal || "",
      telefonoLegal: empresa.telefonoLegal || "",
      faxLegal: empresa.faxLegal || "",
      calleReal: empresa.calleReal || "",
      cpReal: empresa.cpReal || "",
      provinciaReal: empresa.provinciaReal || "",
      departamentoReal: empresa.departamentoReal || "",
      localidadReal: empresa.localidadReal || "",
      telefonoReal: empresa.telefonoReal || "",
      faxReal: empresa.faxReal || "",
      inicio: periodos?.inicio || "",
      fin: periodos?.fin || "",
      anio: periodos?.anio ? Number(periodos?.anio) : undefined,
      cantidadManifiestos: periodos?.cantidadManifiestos
        ? Number(periodos?.cantidadManifiestos)
        : undefined,
      actividad: actividad.replace(/_/g, "/").replace(/-/g, " ") || "",
      equipamientos: listadoEquipamiento?.filter((equip) => !equip.borrado).map((equip) => ({
        // id: equip.id || undefined,
        tipo: equip.tipo,
        potencia: equip.potencia,
        descripcion: equip.descripcion,
        cantidad: equip.cantidad,
      })),
      materiasPrimas: listadoMateriaPrima?.filter((materia) => !materia.borrado).map((materia) => ({
        // id: materia.id || undefined,
        compuestoQuimico: materia.compuestoQuimico,
        unidad: materia.unidad,
        estado: materia.estado,
        cantidadAnual: materia.cantidadAnual,
      })),
      efluentesGaseosos: listadoEfluentes
        .filter((efluente) => !efluente.id || (efluente.aBorrar === true && efluente.id))
        ?.map((efluente) => {
          if (efluente.aBorrar === true) {
            return { id: efluente.id, aBorrar: true };
          } else {
            return {
              id: efluente.id || undefined,
              tipoDeResiduo: efluente.tipoDeResiduo || "",
              descripcion: efluente.descripcion,
              dispositivoDeMedicion: efluente.dispositivoDeMedicion || false,
              dispositivoDeDepuracion: efluente.dispositivoDeDepuracion || false,
              frecuenciaDeEmisiones: efluente.frecuenciaDeEmisiones || "",
              tipoDeDispositivoDepuracion: efluente.tipoDeDispositivoDepuracion || "",
              tipoDeDispositivoMedicion: efluente.tipoDeDispositivoMedicion || "",
              observaciones: efluente.observaciones || "",
              fileMedicion: efluente.fileMedicion || null,
              fileDepuracion: efluente.fileDepuracion || null,
            };
          }
        }),
      infraestructura: infraestructura,
      vehiculos: listadoVehiculos.filter((item) => !item.aBorrar),
      medidasDeSeguridad: listadoMedidas.filter((item) => !item.aBorrar),
      residuosGenerados: listadoResiduosGenerados
        .filter((item) => !item.aBorrar)
        .map(({ empresaTransportista, ...residuo }) => residuo),
      residuosReciclados: listadoResiduosReciclados.filter((item) => !item.aBorrar),
      residuosTransportados: listadoResiduosTransportados.filter((item) => !item.aBorrar),
    };
    saveDeclaracionJurada({ variables: { input } });
  };
  const actualizarEstado = (estado) => {
    setEstadoGuardado(estado);
    const input = {
      id: idDeclaracion,
      estado: estado,
    };
    actualizarEstadoDeclaracionJurada({ variables: { input } });
  };

  const handleModalExitoConfirm = () => {
    window.location.href = `/declaraciones-juradas`;
  };

  useEffect(() => {
    if (idEmpresa) {
      getEmpresa({ variables: { id: idEmpresa } }).catch((queryError) => {
        console.error("Error al obtener la empresa:", queryError);
      });
    }
  }, [idEmpresa, getEmpresa]);
  useEffect(() => {
    setIsDeclaracionReady(idDeclaracion === "nuevo");
  }, [idDeclaracion]);

  useEffect(() => {
    if (idDeclaracion !== "nuevo") {
      getDeclaracion({ variables: { id: idDeclaracion } })
        .catch((queryError) => {
          console.error("Error al obtener la declaración jurada:", queryError);
        })
        .finally(() => {
          setIsDeclaracionReady(true);
        });
    } else if (idDeclaracion === "nuevo" && idEmpresa) {
      getResiduosTransportados({ variables: { idEmpresa } }).catch((queryError) => {
        console.error("Error al obtener residuos transportados:", queryError);
      });
    }
  }, [idDeclaracion, idEmpresa, getDeclaracion, getResiduosTransportados]);

  useEffect(() => {
    const declaracion = dataDeclaracion?.declaracionJurada;
    if (!declaracion) return;

    setListadoEquipamiento(declaracion.equipamientos || []);
    setListadoMateriaPrima(declaracion.materiasPrimas || []);
    setListadoEfluentes(declaracion.efluentesGaseosos || []);
    setInfraestructura(declaracion.infraestructura || {});
    setListadoVehiculos(declaracion.vehiculos || []);
    setListadoMedidas(declaracion.medidasDeSeguridad || []);
    setListadoResiduosGenerados(declaracion.residuosGenerados || []);
    setListadoResiduosReciclados(declaracion.residuosReciclados || []);
    setListadoResiduosTransportados(declaracion.residuosTransportados || []);
    setPeriodos({
      inicio: declaracion.inicio || "",
      fin: declaracion.fin || "",
      anio: declaracion.anio || null,
      cantidadManifiestos: declaracion.cantidadManifiestos || null,
    });
  }, [dataDeclaracion]);

  useEffect(() => {
    if (data?.empresa) {
      if (dataDeclaracion?.declaracionJurada?.id) {
        setEmpresa({
          ...data.empresa,
          calleLegal: dataDeclaracion.declaracionJurada.calleLegal,
          cpLegal: dataDeclaracion.declaracionJurada.cpLegal,
          provinciaLegal: dataDeclaracion.declaracionJurada.provinciaLegal,
          departamentoLegal: dataDeclaracion.declaracionJurada.departamentoLegal,
          localidadLegal: dataDeclaracion.declaracionJurada.localidadLegal,
          telefonoLegal: dataDeclaracion.declaracionJurada.telefonoLegal,
          faxLegal: dataDeclaracion.declaracionJurada.faxLegal,
          calleReal: dataDeclaracion.declaracionJurada.calleReal,
          cpReal: dataDeclaracion.declaracionJurada.cpReal,
          provinciaReal: dataDeclaracion.declaracionJurada.provinciaReal,
          departamentoReal: dataDeclaracion.declaracionJurada.departamentoReal,
          localidadReal: dataDeclaracion.declaracionJurada.localidadReal,
          telefonoReal: dataDeclaracion.declaracionJurada.telefonoReal,
          faxReal: dataDeclaracion.declaracionJurada.faxReal,
        });
      } else {
        setEmpresa(data.empresa);
      }
    }
  }, [data, dataDeclaracion]);

  if (loading) {
    return (
      <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <SoftTypography variant="h6">Cargando datos de la empresa...</SoftTypography>;
      </SoftBox>
    );
  }

  if (error) {
    return (
      <SoftTypography variant="h6" color="error">
        Error al cargar los datos de la empresa.
      </SoftTypography>
    );
  }

  if (!isDeclaracionReady) {
    return (
      <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <SoftTypography variant="h6">Cargando declaración jurada...</SoftTypography>
      </SoftBox>
    );
  }
  if (idDeclaracion !== "nuevo" && (errorDeclaracion || !dataDeclaracion?.declaracionJurada)) {
    return (
      <SoftTypography variant="h6" color="error">
        No se pudo cargar la declaración jurada.
      </SoftTypography>
    );
  }
  const finalizada =
    dataDeclaracion?.declaracionJurada?.estado === "aceptada" ||
    dataDeclaracion?.declaracionJurada?.estado === "presentada" ||
    !["Empresa", "Responsable", "Administrador"].includes(user?.tipoUser);
  const transportista = actividad == "Transportista";
  const presentada = dataDeclaracion?.declaracionJurada?.estado === "presentada";
  const actividadFormateada = actividad.replace(/_/g, "/").replace(/-/g, " ");
  return (
    <PageLayout>
      <Card
        sx={{
          m: 1,
        }}
      >
        <Grid
          display="flex"
          justifyContent="center"
          alignItems="center"
          px={3}
          flexDirection="column"
        >
          <SoftTypography variant="h4" fontWeight="bold">
            Declaración Jurada de Residuos Peligrosos
          </SoftTypography>
          <SoftTypography variant="h4" fontWeight="bold">
            {actividad.replace(/_/g, "/").replace(/-/g, " ")}
          </SoftTypography>
        </Grid>
        <Grid p={2}>
          <SoftTypography variant="h4" fontWeight="bold" mb={1.5}>
            Datos de la Empresa
          </SoftTypography>
          {empresa ? (
            <Grid container spacing={1.5} sx={{ overflowWrap: "anywhere" }}>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Razón Social:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.razonSocial || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  CUIT:
                </SoftTypography>
                <SoftTypography variant="body2">{empresa.cuit || "Sin información"}</SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Email:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.email || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Gerente:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.gerente || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Código AFIP:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.codigoAfip || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Registro Catastral:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.registroCatastral || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Habilitación Municipal Número:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.habilitacionMunicipalNumero || "Sin información"}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <SoftTypography variant="subtitle2" fontWeight="bold">
                  Habilitación Municipal Lugar:
                </SoftTypography>
                <SoftTypography variant="body2">
                  {empresa.habilitacionMunicipalLugar || "Sin información"}
                </SoftTypography>
              </Grid>
              {
                <Grid item xs={12} display="flex">
                  <SoftButton color="primary" onClick={() => setModalDomicilio(true)}>
                    {!verificarAcceso(207) ? "Ver" : "Modificar"} Domicilio legal/real
                  </SoftButton>
                </Grid>
              }
            </Grid>
          ) : (
            <SoftTypography variant="h6">No se encontraron datos de la empresa.</SoftTypography>
          )}
        </Grid>
      </Card>
      <Card sx={{ m: 1 }}>
        <Grid p={2} container spacing={1}>
          <Grid item xs={12}>
            <SoftTypography variant="h4" fontWeight="bold">
              Periodos
            </SoftTypography>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <InputLabel htmlFor="inicio">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Inicio
              </SoftTypography>
            </InputLabel>
            {finalizada ? (
              <SoftTypography>{periodos?.inicio || ""}</SoftTypography>
            ) : (
              <SoftInput
                id="inicio"
                placeholder="Mes inicio"
                fullWidth
                name="inicio"
                value={periodos?.inicio || ""}
                onChange={(e) => {
                  setPeriodos((prev) => ({ ...(prev || {}), inicio: e.target.value }));
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <InputLabel htmlFor="fin">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Fin
              </SoftTypography>
            </InputLabel>
            {finalizada ? (
              <SoftTypography>{periodos?.fin || ""}</SoftTypography>
            ) : (
              <SoftInput
                id="fin"
                placeholder="Mes fin"
                fullWidth
                name="fin"
                value={periodos?.fin || ""}
                onChange={(e) => {
                  setPeriodos((prev) => ({ ...(prev || {}), fin: e.target.value }));
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <InputLabel htmlFor="anio">
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Año
              </SoftTypography>
            </InputLabel>
            {finalizada ? (
              <SoftTypography>{periodos?.anio || ""}</SoftTypography>
            ) : (
              <SoftInputNumber
                id="anio"
                placeholder="Año"
                fullWidth
                name="anio"
                value={asNumberOrNull(periodos?.anio)}
                onChange={(e) => {
                  setPeriodos((prev) => ({ ...(prev || {}), anio: e.target.value }));
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <InputLabel htmlFor="cantidadManifiestos" sx={{ whiteSpace: "normal" }}>
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                Cantidad de manifiestos utilizados
              </SoftTypography>
            </InputLabel>
            {finalizada ? (
              <SoftTypography>{periodos?.cantidadManifiestos || ""}</SoftTypography>
            ) : (
              <SoftInputNumber
                id="cantidadManifiestos"
                placeholder="Cantidad"
                fullWidth
                name="cantidadManifiestos"
                value={asNumberOrNull(periodos?.cantidadManifiestos)}
                onChange={(e) => {
                  setPeriodos((prev) => ({ ...(prev || {}), cantidadManifiestos: e.target.value }));
                }}
              />
            )}
          </Grid>
        </Grid>
      </Card>
      {!transportista && (
        <Card sx={{ m: 1 }}>
          <Equipamiento
            idDeclaracion={idDeclaracion}
            listadoEquipamiento={listadoEquipamiento}
            setListadoEquipamiento={setListadoEquipamiento}
            finalizada={finalizada}
            idEmpresa={idEmpresa}
          />
        </Card>
      )}
      {!transportista && (
        <Card sx={{ m: 1 }}>
          <MateriaPrima
            idDeclaracion={idDeclaracion}
            listadoMateriaPrima={listadoMateriaPrima}
            setListadoMateriaPrima={setListadoMateriaPrima}
            finalizada={finalizada}
            idEmpresa={idEmpresa}
          />
        </Card>
      )}
      <Card sx={{ m: 1 }}>
        <Infraestructura
          infraestructura={infraestructura}
          setInfraestructura={setInfraestructura}
          finalizada={finalizada}
        />
      </Card>
      {!transportista && (
        <Card sx={{ m: 1 }}>
          <Efluentes
            idDeclaracion={idDeclaracion}
            listadoEfluentes={listadoEfluentes}
            setListadoEfluentes={setListadoEfluentes}
            finalizada={finalizada}
          />
        </Card>
      )}
      {(actividad === "Generadora" || actividadFormateada == "Generador Comercio/Servicio") && (
        <Card sx={{ m: 1 }}>
          <ResiduosGenerados
            idDeclaracion={idDeclaracion}
            listadoResiduosGenerados={listadoResiduosGenerados}
            setListadoResiduosGenerados={setListadoResiduosGenerados}
            finalizada={finalizada}
          />
        </Card>
      )}
      {actividad.includes("Operador") && (
        <Card sx={{ m: 1 }}>
          <ResiduosReciclados
            idDeclaracion={idDeclaracion}
            listadoResiduosReciclados={listadoResiduosReciclados}
            setListadoResiduosReciclados={setListadoResiduosReciclados}
            finalizada={finalizada}
          />
        </Card>
      )}
      {transportista && (
        <>
          <Card sx={{ m: 1 }}>
            <ResiduosTransportados
              idDeclaracion={idDeclaracion}
              listadoResiduosTransportados={listadoResiduosTransportados}
              setListadoResiduosTransportados={setListadoResiduosTransportados}
              finalizada={finalizada}
              generados={dataTransportados?.residuosTransportados || []}
            />
          </Card>
          <Card sx={{ m: 1 }}>
            <Vehiculos
              idDeclaracion={idDeclaracion}
              listadoVehiculos={listadoVehiculos}
              setListadoVehiculos={setListadoVehiculos}
              idEmpresa={idEmpresa}
              finalizada={finalizada}
            />
          </Card>
        </>
      )}
      <Card sx={{ m: 1 }}>
        <MedidasDeSeguridad
          idDeclaracion={idDeclaracion}
          listadoMedidas={listadoMedidas}
          setListadoMedidas={setListadoMedidas}
          finalizada={finalizada}
        />
      </Card>
      <Grid p={3} display={"flex"} justifyContent="flex-end">
        <SoftButton
          variant="gradient"
          onClick={() => {
            window.location.href = `/declaraciones-juradas`;
          }}
          sx={{
            mr: 2,
            background: getColor("dark"),
            color: "#fff",
          }}
          disabled={saving}
        >
          {finalizada ? "Cerrar" : "Salir sin guardar"}
        </SoftButton>
        {!finalizada && (
          <>
            <SoftButton
              variant="gradient"
              onClick={() => handleSave("borrador")}
              sx={{
                mr: 2,
                background: getColor("gobSecondary"),
                color: "#fff",
              }}
              disabled={saving}
            >
              Borrador
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="primary"
              onClick={() => handleSave("presentada")}
              disabled={saving}
            >
              Presentar
            </SoftButton>
          </>
        )}
        {presentada &&
          verificarAcceso(204) && (
          <>
            <SoftButton
              variant="gradient"
              onClick={() => actualizarEstado("borrador")}
              sx={{
                mr: 2,
                background: getColor("gobSecondary"),
                color: "#fff",
              }}
              disabled={savingUpdate}
            >
              Regresar a Borrador
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="primary"
              onClick={() => actualizarEstado("aceptada")}
              disabled={savingUpdate}
            >
              Aceptar declaración
            </SoftButton>
          </>
        )}
        <ModalDomicilio
          open={modalDomicilio}
          handleClose={() => setModalDomicilio(false)}
          empresa={empresa}
          setEmpresa={setEmpresa}
          finalizada={idDeclaracion === "nuevo" || presentada || dataDeclaracion?.declaracionJurada?.estado === "aceptada"}
          user={user}
          saving={saving}
          onSave={() => {
            setEstadoGuardado("borrador");
            saveDeclaracionJurada({ variables: { input: {
              id: idDeclaracion,
              ...Object.fromEntries(Object.entries(empresa || {}).filter(([key]) => /^(calle|cp|provincia|departamento|localidad|telefono|fax)(Legal|Real)$/.test(key))),
            } } });
          }}
        />
        <ModalConfirm
          open={modalExito}
          handleClose={() => setModalExito(false)}
          title="Declaración Jurada Presentada"
          message="Su Declaración Jurada ha sido presentada. Luego de 48 hs hábiles, la misma será categorizada y podrá descargarla con el comprobante de la tasa correspondiente."
          func={handleModalExitoConfirm}
          icon="check_circle"
        />
      </Grid>
    </PageLayout>
  );
}

export default DeclaracionJurada;
